import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { parseApiErrorBody } from '@/api/v2Client';
import {
  compilerApi,
  type SubmissionLinkKind,
  type SubmissionLinksResponse,
  type SubmissionLinkStatus,
} from '@/lib/api/compiler';

/**
 * phase-links-submission-view — the centre pane's Links tab: the
 * SUBMISSION-WIDE links worklist (Sharon's ruling, 2026-07-29/30).
 *
 * One row per link annotation across every document of the current sequence,
 * evaluated server-side by the same WAVE-E4 evaluator the Inspector's
 * per-document tab reports from. Worklist idiom (matching the validation
 * drawer): broken rows sort first — the exceptional case is loud, the routine
 * case is quiet; filters by status / kind / source document; clicking a row
 * drills down to the SOURCE document (selects it in the CTD tree and opens the
 * Inspector's per-document links tab).
 *
 * Genuinely distinct from the Inspector tab: this is "show me all the broken
 * links in the sequence"; the Inspector is "this document's links".
 */

export interface SubmissionLinksPanelProps {
  readonly submissionId: string;
  /** Drill-down: select the source document + open its Inspector links tab. */
  readonly onOpenDocumentLinks: (documentId: string) => void;
}

type StatusFilter = 'all' | SubmissionLinkStatus;
type KindFilter = 'all' | SubmissionLinkKind;

// Worklist order: broken loudest, then the "could not fully verify" statuses
// (incomplete, remote-unverified), then the quiet routine ones. Every
// SubmissionLinkStatus must have a rank or the sort comparator returns NaN.
const STATUS_RANK: Readonly<Record<SubmissionLinkStatus, number>> = {
  broken: 0,
  incomplete: 1,
  'remote-unverified': 2,
  resolves: 3,
  external: 4,
};

const panelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minHeight: 0,
  flex: 1,
};

const summaryStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  fontSize: 13,
};

const pillStyle: CSSProperties = {
  padding: '2px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  border: '1px solid var(--border)',
};

const filterBarStyle: CSSProperties = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  alignItems: 'center',
};

const selectStyle: CSSProperties = {
  fontSize: 12,
  padding: '4px 6px',
  borderRadius: 4,
  border: '1px solid var(--border)',
  background: 'var(--bg)',
  color: 'var(--text)',
};

const rowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(140px, 1.2fr) 90px minmax(160px, 1.6fr) 110px',
  gap: 8,
  alignItems: 'baseline',
  width: '100%',
  textAlign: 'left',
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface)',
  cursor: 'pointer',
  fontSize: 13,
  color: 'var(--text)',
};

const mutedStyle: CSSProperties = { color: 'var(--text-muted)', fontSize: 12 };

const emptyStyle: CSSProperties = {
  padding: '18px 12px',
  fontSize: 13,
  color: 'var(--text-muted)',
  fontStyle: 'italic',
  textAlign: 'center',
  border: '1px dashed var(--border)',
  borderRadius: 6,
};

function statusBadge(status: SubmissionLinkStatus): ReactElement {
  const cfg =
    status === 'broken'
      ? { label: 'Broken', bg: '#fee2e2', border: '#fca5a5', color: '#7f1d1d' }
      : status === 'resolves'
        ? { label: 'Resolves', bg: '#ecfdf5', border: '#a7f3d0', color: '#065f46' }
        : status === 'remote-unverified'
          ? { label: 'Remote · not verified', bg: '#fef3c7', border: '#fcd34d', color: '#92400e' }
          : status === 'incomplete'
            ? { label: 'Not fully verified', bg: '#fef3c7', border: '#fcd34d', color: '#92400e' }
            : { label: 'External · not checked', bg: 'var(--surface2)', border: 'var(--border)', color: 'var(--text-muted)' };
  return (
    <span
      style={{
        ...pillStyle,
        background: cfg.bg,
        borderColor: cfg.border,
        color: cfg.color,
        justifySelf: 'start',
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.label}
    </span>
  );
}

function kindLabel(kind: SubmissionLinkKind): string {
  switch (kind) {
    case 'page-anchor':
      return 'internal';
    case 'cross-document':
      return 'cross-doc';
    case 'external':
      return 'external';
    case 'remote':
      return 'remote';
    default:
      return 'unknown';
  }
}

export function SubmissionLinksPanel({
  submissionId,
  onOpenDocumentLinks,
}: SubmissionLinksPanelProps): ReactElement {
  const [data, setData] = useState<SubmissionLinksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setData(await compilerApi.submissionLinks(submissionId));
    } catch (err) {
      const body = parseApiErrorBody(err);
      setError(
        body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Source-document filter options come from the row set itself.
  const sourceOptions = useMemo(() => {
    if (!data) return [];
    const seen = new Map<string, string>();
    for (const l of data.links) seen.set(l.sourceDocumentId, l.sourceFileName);
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [data]);

  // Documents that produced at least one row — used to tell a partially
  // analysed document (truncated scan, rows present) apart from one we could
  // not read at all (no rows). See the unanalysed block below.
  const linkedDocIds = useMemo(() => {
    const s = new Set<string>();
    if (data) for (const l of data.links) s.add(l.sourceDocumentId);
    return s;
  }, [data]);

  // Broken first (loud), then resolves, then external (quiet); stable within
  // a status by source name then page.
  const visibleRows = useMemo(() => {
    if (!data) return [];
    return data.links
      .filter((l) => statusFilter === 'all' || l.status === statusFilter)
      .filter((l) => kindFilter === 'all' || l.kind === kindFilter)
      .filter((l) => sourceFilter === 'all' || l.sourceDocumentId === sourceFilter)
      .slice()
      .sort(
        (a, b) =>
          STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
          a.sourceFileName.localeCompare(b.sourceFileName) ||
          a.page - b.page,
      );
  }, [data, statusFilter, kindFilter, sourceFilter]);

  if (loading && !data) {
    return (
      <div data-testid="submission-links-loading" style={emptyStyle}>
        Analysing links across the sequence…
      </div>
    );
  }
  if (error && !data) {
    return (
      <div data-testid="submission-links-error" role="alert" style={{ ...emptyStyle, color: 'var(--danger, #b91c1c)', fontStyle: 'normal' }}>
        Could not load the submission’s links: {error}
      </div>
    );
  }
  if (!data) return <div style={emptyStyle}>No data.</div>;

  const { counts, unanalysed } = data;

  return (
    <div data-testid="submission-links-panel" style={panelStyle}>
      {/* Summary counts — derived server-side from the same row set. */}
      <div data-testid="submission-links-summary" style={summaryStyle}>
        <strong>
          {counts.total} link{counts.total === 1 ? '' : 's'}
        </strong>
        <span
          data-testid="submission-links-broken-count"
          style={{
            ...pillStyle,
            background: counts.broken > 0 ? '#fee2e2' : '#ecfdf5',
            borderColor: counts.broken > 0 ? '#fca5a5' : '#a7f3d0',
            color: counts.broken > 0 ? '#7f1d1d' : '#065f46',
          }}
        >
          {counts.broken} broken
        </span>
        <span style={mutedStyle}>
          {counts.resolves} resolve{counts.resolves === 1 ? 's' : ''} ·{' '}
          {counts.external} external (not checked)
          {counts.remoteUnverified > 0 && ` · ${counts.remoteUnverified} remote (not verified)`}
          {counts.incomplete > 0 && ` · ${counts.incomplete} not fully verified`}
        </span>
        <button
          type="button"
          data-testid="submission-links-refresh"
          onClick={() => void load()}
          disabled={loading}
          style={{ ...selectStyle, cursor: loading ? 'wait' : 'pointer', marginLeft: 'auto' }}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* U03 finding 7 — a refresh that fails while we still hold data must not
          swallow the error: show it as a banner and keep the (now stale) rows
          visible rather than silently presenting them as current. */}
      {error && (
        <div
          data-testid="submission-links-refresh-error"
          role="alert"
          style={{
            padding: '8px 10px',
            borderRadius: 6,
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            color: '#7f1d1d',
            fontSize: 12,
          }}
        >
          Could not refresh links: {error}. Showing the last loaded results.
        </div>
      )}

      {/* Filters — worklist idiom, matching the validation drawer. */}
      <div style={filterBarStyle}>
        <label style={mutedStyle}>
          Status{' '}
          <select
            data-testid="submission-links-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            style={selectStyle}
          >
            <option value="all">All</option>
            <option value="broken">Broken</option>
            <option value="incomplete">Not fully verified</option>
            <option value="remote-unverified">Remote (not verified)</option>
            <option value="resolves">Resolves</option>
            <option value="external">External</option>
          </select>
        </label>
        <label style={mutedStyle}>
          Kind{' '}
          <select
            data-testid="submission-links-filter-kind"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as KindFilter)}
            style={selectStyle}
          >
            <option value="all">All</option>
            <option value="page-anchor">Internal (page anchor)</option>
            <option value="cross-document">Cross-document</option>
            <option value="external">External</option>
            <option value="remote">Remote</option>
            <option value="unknown">Unknown</option>
          </select>
        </label>
        <label style={mutedStyle}>
          Source document{' '}
          <select
            data-testid="submission-links-filter-source"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="all">All</option>
            {sourceOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Word-native results present → the honesty caveat + advisory note.
          Advisory rows are labelled Word-source checks (B.6): they ran against
          the Word source and the final artifact was not verified. The two
          result kinds are never presented as one truth. */}
      {data.docxCheckCaveat !== null && (
        <div
          data-testid="submission-links-docx-caveat"
          style={{
            padding: '8px 10px',
            borderRadius: 6,
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            color: 'var(--text-muted)',
            fontSize: 12,
          }}
        >
          <strong>Word-native checks included.</strong> {data.docxCheckCaveat}
          {counts.advisory > 0 && (
            <>
              {' '}
              {counts.advisory} result{counts.advisory === 1 ? ' is' : 's are'} an{' '}
              <em>advisory Word-source check</em> on a Word file bound for a PDF slot —
              the final artifact has not been verified.
            </>
          )}
        </div>
      )}

      {/* Documents that could not be analysed — pending/honest, never absent.
          U03 finding 6 — a truncated scan still emits the rows it DID find, so
          it appears here AND in the row set. Those are "partially analysed"
          (results may be incomplete), distinct from documents we could not read
          at all ("not analysed"). Collapsing them into one "not analysed"
          heading would wrongly imply the partial document's rows are absent. */}
      {unanalysed.length > 0 && (() => {
        const partial = unanalysed.filter((u) => linkedDocIds.has(u.documentId));
        const wholly = unanalysed.filter((u) => !linkedDocIds.has(u.documentId));
        return (
          <div
            data-testid="submission-links-unanalysed"
            style={{
              padding: '8px 10px',
              borderRadius: 6,
              background: '#fef9c3',
              border: '1px solid #fde68a',
              color: '#854d0e',
              fontSize: 12,
            }}
          >
            {wholly.length > 0 && (
              <div data-testid="submission-links-unanalysed-wholly">
                <strong>
                  {wholly.length} document{wholly.length === 1 ? '' : 's'} not analysed
                </strong>{' '}
                — links there are unknown, not absent:
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
                  {wholly.map((u) => (
                    <li key={u.documentId}>
                      <span style={{ fontFamily: 'monospace' }}>{u.fileName}</span> — {u.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {partial.length > 0 && (
              <div
                data-testid="submission-links-unanalysed-partial"
                style={{ marginTop: wholly.length > 0 ? 6 : 0 }}
              >
                <strong>
                  {partial.length} document{partial.length === 1 ? '' : 's'} partially analysed
                </strong>{' '}
                — the links below may be incomplete:
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
                  {partial.map((u) => (
                    <li key={u.documentId}>
                      <span style={{ fontFamily: 'monospace' }}>{u.fileName}</span> — {u.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })()}

      {/* Rows */}
      {data.links.length === 0 ? (
        <div data-testid="submission-links-empty" style={emptyStyle}>
          {data.documentCount === 0
            ? 'This sequence has no documents yet.'
            : 'No hyperlinks found in any analysed document of this sequence.'}
        </div>
      ) : visibleRows.length === 0 ? (
        <div data-testid="submission-links-no-match" style={emptyStyle}>
          No links match the current filters.
        </div>
      ) : (
        <div
          data-testid="submission-links-rows"
          style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', minHeight: 0 }}
        >
          {visibleRows.map((l, idx) => (
            <button
              key={`${l.sourceDocumentId}-${l.page}-${idx}`}
              type="button"
              data-testid={`submission-links-row-${idx}`}
              data-status={l.status}
              onClick={() => onOpenDocumentLinks(l.sourceDocumentId)}
              title="Open this document’s links in the Inspector"
              style={{
                ...rowStyle,
                ...(l.status === 'broken' ? { borderColor: '#fca5a5' } : null),
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {l.sourceFileName}
                <span style={mutedStyle}>
                  {/* Word has no pre-render pages; page 0 = document-level. */}
                  {l.method === 'docx' ? ' · Word' : ` · p.${l.page}`}
                </span>
                {l.check === 'advisory' && (
                  <span
                    data-testid={`submission-links-advisory-${idx}`}
                    title="Advisory check on the Word source — the final artifact has not been verified"
                    style={{
                      ...pillStyle,
                      marginLeft: 6,
                      fontSize: 10,
                      padding: '1px 6px',
                      background: 'var(--surface2)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    advisory
                  </span>
                )}
              </span>
              <span style={mutedStyle}>{kindLabel(l.kind)}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                → {l.target}
                {l.reason !== null && (
                  <span style={{ ...mutedStyle, color: '#b91c1c' }}> — {l.reason}</span>
                )}
              </span>
              {statusBadge(l.status)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
